import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { monitoringApi, AwaitingItem, Classification, MonitoringApiError, ListParams } from '../../../../services/monitoringApi';
import logger from '../../../../utils/logger';

export interface MonitoringListFilters {
    riskLevel: string;
    active: string;
    search: string;
    personName: string;
    hasDivergence: string;
    dateFrom: string;
    dateTo: string;
    sortBy: string;
    sortOrder: 'ASC' | 'DESC' | '';
}

const AWAITING_PAGE_SIZE = 100;

/**
 * Teto defensivo, não um limite de negócio.
 *
 * O número de páginas a buscar é derivado do `total` devolvido pela API, então em operação
 * normal este valor nunca é alcançado. Ele existe só para o laço não ficar indefinido se a
 * API reportar um `total` incoerente com o que devolve.
 */
const AWAITING_HARD_PAGE_LIMIT = 500;

const indexByTaxId = (items: AwaitingItem[]): Record<string, AwaitingItem> => {
    const map: Record<string, AwaitingItem> = {};
    items.forEach(item => {
        if (item.tax_id) map[item.tax_id] = item;
    });
    return map;
};

/**
 * Busca todas as páginas de documentos aguardando o provedor.
 *
 * O endpoint limita pageSize a 100. Buscar só a primeira página deixaria o 101º documento
 * em diante sem o estado de espera, e a célula exibiria uma data de próximo monitoramento
 * que a varredura vai deliberadamente ignorar — informação errada, não apenas ausente.
 *
 * O número de páginas vem do `total` da primeira resposta. Um teto fixo aqui recriaria o
 * mesmo defeito num volume maior: bastaria o backlog passar do teto para parte dos
 * documentos voltar a exibir data incorreta, e de forma silenciosa.
 */
export const fetchAllAwaiting = async (): Promise<AwaitingItem[]> => {
    const first = await monitoringApi.getPending({ page: 1, pageSize: AWAITING_PAGE_SIZE });
    const collected: AwaitingItem[] = first.data?.items || [];
    const total: number = first.data?.total ?? collected.length;

    const pages = Math.min(
        Math.ceil(total / AWAITING_PAGE_SIZE),
        AWAITING_HARD_PAGE_LIMIT
    );

    for (let page = 2; page <= pages; page += 1) {
        const result = await monitoringApi.getPending({ page, pageSize: AWAITING_PAGE_SIZE });
        const items: AwaitingItem[] = result.data?.items || [];
        if (items.length === 0) break;
        collected.push(...items);
    }

    if (collected.length < total) {
        // Não fica silencioso: os documentos que faltaram vão exibir a data de próximo
        // monitoramento, que a varredura vai ignorar enquanto eles estiverem em voo.
        logger.warn(
            `Monitoramento: ${collected.length} de ${total} documentos aguardando foram carregados. ` +
            'A coluna Próx. Mon. pode exibir data para documentos ainda em voo.'
        );
    }

    return collected;
};

export default function useMonitoringList() {
    const navigate = useNavigate();
    const [rows, setRows] = useState<Classification[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    const [isLoading, setIsLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [refreshKey, setRefreshKey] = useState(0);
    // AF-3581: documentos aguardando retorno do Data Provider, indexados por tax_id.
    // A listagem de classificações não conhece esse estado, então vem da rota de pendentes.
    const [awaitingByTaxId, setAwaitingByTaxId] = useState<Record<string, AwaitingItem>>({});

    const [filters, setFilters] = useState<MonitoringListFilters>({
        riskLevel: '',
        active: 'true',
        search: '',
        personName: '',
        hasDivergence: '',
        dateFrom: '',
        dateTo: '',
        sortBy: '',
        sortOrder: '',
    });

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const params: ListParams = {
                page,
                pageSize,
                riskLevel: filters.riskLevel || undefined,
                active: filters.active === '' ? undefined : filters.active === 'true',
                search: filters.search ? filters.search.replace(/[.\-/\s]/g, '').toUpperCase() : undefined,
                personName: filters.personName || undefined,
                hasDivergence: filters.hasDivergence === '' ? undefined : filters.hasDivergence === 'true',
                dateFrom: filters.dateFrom || undefined,
                dateTo: filters.dateTo || undefined,
                sortBy: filters.sortBy || undefined,
                sortOrder: filters.sortOrder || undefined,
            };
            const result = await monitoringApi.list(params);
            setRows(result.data?.items || []);
            setTotal(result.data?.total || 0);
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                if (e.status === 403) { navigate('/denied'); return; }
                if (e.status === 401) { navigate('/login'); return; }
            }
            toast.error('Erro ao carregar lista de monitoramento');
            setRows([]);
            setTotal(0);
        } finally {
            setIsLoading(false);
            setInitialLoading(false);
        }
    }, [page, pageSize, filters, navigate, refreshKey]);

    const fetchAwaiting = useCallback(async () => {
        try {
            setAwaitingByTaxId(indexByTaxId(await fetchAllAwaiting()));
        } catch {
            // Estado auxiliar: a listagem principal continua utilizável sem ele, então
            // uma falha aqui não vira toast de erro para o usuário.
            setAwaitingByTaxId({});
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { fetchAwaiting(); }, [fetchAwaiting, refreshKey]);

    const refresh = () => setRefreshKey(k => k + 1);

    const updateFilter = (key: keyof MonitoringListFilters, value: string) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPage(1);
    };

    const clearFilters = () => {
        setFilters({
            riskLevel: '',
            active: '',
            search: '',
            personName: '',
            hasDivergence: '',
            dateFrom: '',
            dateTo: '',
            sortBy: '',
            sortOrder: '',
        });
        setPage(1);
        setRefreshKey(k => k + 1);
    };

    const toggleSort = (field: string) => {
        setFilters(prev => {
            if (prev.sortBy === field) {
                // Same column: toggle direction
                const nextOrder = prev.sortOrder === 'ASC' ? 'DESC' : 'ASC';
                return { ...prev, sortOrder: nextOrder };
            }
            // Different column: set ASC
            return { ...prev, sortBy: field, sortOrder: 'ASC' };
        });
        setPage(1);
    };

    const totalPages = Math.max(1, Math.ceil(total / pageSize));

    return {
        rows,
        total,
        page,
        setPage,
        pageSize,
        totalPages,
        isLoading,
        initialLoading,
        filters,
        updateFilter,
        clearFilters,
        refresh,
        toggleSort,
        awaitingByTaxId,
    };
}
